import React, { useMemo, useState } from "react";

import LiveTvIcon from "@material-ui/icons/LiveTv";
import { mdiPlusCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Tabs, TabsList, TabsTrigger } from "@/src/components/ui/tabs";

import NewPlatformModal from "./NewPlatformModal";
import { createComponent } from "../../../../api/legacy/ComponentAPI";
import ProjectAPI from "../../../../api/ProjectAPI";
import AppleIcon from "../../../../components/common/icons/AppleIcon";
import GoogleIcon from "../../../../components/common/icons/GoogleIcon";
import WebIcon from "../../../../components/common/icons/WebIcon";
import "../../../../css/admin/platform.scss";

import type { JSX } from "react";

import type { PlatformOption } from "./NewPlatformModal";
import type {
  GetProjectPageResponse,
  Platform,
} from "../../../../api/ProjectAPI.types";
import type { AppState } from "../../../../reducers";

interface PlatformTabsProps {
  selectedPlatform: Platform;
  switchPlatform: (platform: Platform) => void;
}

interface PlatformRoutesParams {
  projectSlug: string;
  pageId: string;
}

function PlatformTabs({
  selectedPlatform,
  switchPlatform,
}: PlatformTabsProps): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const token = useSelector((state: AppState) => state.loginReducer.token);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [temporaryPlatformList, setTemporaryPlatformList] = useState<string[]>(
    [],
  );
  const { projectSlug, pageId } = useParams<PlatformRoutesParams>();

  const isNew = pageId === "new";

  const getProjectPageQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPage,
    params: [{ pageId, projectSlug }],
    options: {
      enabled: !isNew,
    },
  });

  const { data: projectPageData } = useQuery(getProjectPageQuery);

  const platformList = useMemo(() => {
    const platforms =
      projectPageData?.components_platform?.filter(Boolean) ?? [];

    const allPlatforms = new Set(["web"]);

    platforms.forEach((platform) => {
      allPlatforms.add(platform);
    });

    temporaryPlatformList.forEach((platform) => {
      allPlatforms.add(platform);
    });

    return Array.from(allPlatforms);
  }, [temporaryPlatformList, projectPageData?.components_platform]);

  const supportedPlatforms = [
    "web",
    "ios",
    "android",
    "tvos",
    "androidtv",
    "firetv",
    "roku",
    "tizen",
  ];

  const addPlatform = async (
    platform: PlatformOption,
    duplicate: boolean,
    platformDuplication: PlatformOption | null,
  ): Promise<void> => {
    setTemporaryPlatformList((prevTempPlatformList) => {
      if (!prevTempPlatformList.includes(platform.value)) {
        return [...prevTempPlatformList, platform.value];
      }
      return prevTempPlatformList;
    });

    if (duplicate && platformDuplication) {
      const newComponents: Record<string, unknown>[] = [];

      try {
        const getComponentListResponse =
          await ProjectAPI.getProjectPageComponentList({
            projectSlug,
            pageId,
            platform: platformDuplication.value as Platform,
          });

        getComponentListResponse?.components.forEach((component) => {
          const { uuid, ...componentWithoutUuid } = component;
          const newComponent = {
            ...componentWithoutUuid,
            storefront_project_page_uuid: pageId,
            platform: platform.value as Platform,
          };

          newComponents.push(newComponent);
        });

        const promiseList = newComponents.map((newComp) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          dispatch(createComponent(newComp, token, projectSlug)),
        );

        await Promise.all(promiseList);

        queryClient.setQueryData<GetProjectPageResponse>(
          getProjectPageQuery.queryKey,
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              components_platform: [
                ...(oldData?.components_platform ?? []),
                platform.value as Platform,
              ],
            };
          },
        );
      } catch {
        console.error("Error duplicating components");
      }
    }

    switchPlatform(platform.value as Platform);
    setOpenModal(false);
  };

  const platformIcon: Record<string, JSX.Element> = {
    web: <WebIcon />,
    ios: <AppleIcon />,
    android: <GoogleIcon />,
    tvos: <AppleIcon />,
    androidtv: <GoogleIcon />,
    firetv: <LiveTvIcon />,
    roku: <LiveTvIcon />,
    tizen: <LiveTvIcon />,
  };

  const canAddPlatform = platformList.length < supportedPlatforms.length;

  return (
    <React.Fragment>
      <Tabs
        className="mb-1 h-10"
        onValueChange={(value) => switchPlatform(value as Platform)}
        value={selectedPlatform}
      >
        <TabsList>
          {platformList.map((platform) => (
            <TabsTrigger key={platform} className="h-10 px-4" value={platform}>
              {platformIcon[platform]}
              {t(platform)}
            </TabsTrigger>
          ))}
          {canAddPlatform ? (
            <button
              className="ml-2 cursor-pointer"
              onClick={(): void => setOpenModal(true)}
              type="button"
            >
              <Icon path={mdiPlusCircleOutline} size={1} />
            </button>
          ) : null}
        </TabsList>
      </Tabs>
      <NewPlatformModal
        currentPlatforms={platformList}
        isOpen={openModal}
        onClose={(): void => setOpenModal(false)}
        onSubmit={addPlatform}
        supportedPlatforms={supportedPlatforms}
      />
    </React.Fragment>
  );
}

export default PlatformTabs;
